import { Controller } from "@hotwired/stimulus"

// Handles inline editing of individual fix suggestions
export default class extends Controller {
  static targets = ["viewMode", "editMode", "input", "displayValue", "preview", "charCount", "editButton"]
  static values = { fixId: Number }

  connect() {
    this.updatePreview()
  }

  toggleEdit(event) {
    event.preventDefault()
    
    if (this.editModeTarget.classList.contains("hidden")) {
      // Enter edit mode
      this.viewModeTarget.classList.add("hidden")
      this.editModeTarget.classList.remove("hidden")
      this.editButtonTarget.textContent = "Cancel"
      this.inputTarget.focus()
    } else {
      // Exit edit mode
      this.cancelEdit()
    }
  }

  cancelEdit() {
    this.viewModeTarget.classList.remove("hidden")
    this.editModeTarget.classList.add("hidden")
    this.editButtonTarget.textContent = "Edit"
    
    // Reset input to original value
    const originalValue = this.displayValueTarget.textContent.trim()
    this.inputTarget.value = originalValue
  }

  updatePreview() {
    if (!this.hasPreviewTarget) return

    const value = this.inputTarget.value
    const fixType = this.element.dataset.fixType || "title"
    
    // Generate HTML preview
    let html = ""
    switch(fixType) {
      case "title":
        html = `<title>${this.escapeHtml(value)}</title>`
        break
      case "meta_description":
        html = `<meta name="description" content="${this.escapeHtml(value)}">`
        break
      case "h1":
        html = `<h1>${this.escapeHtml(value)}</h1>`
        break
      case "canonical":
        html = `<link rel="canonical" href="${this.escapeHtml(value)}">`
        break
      case "viewport":
        html = `<meta name="viewport" content="${this.escapeHtml(value)}">`
        break
      case "schema":
        try {
          const parsed = JSON.parse(value)
          html = `<script type="application/ld+json">\n${JSON.stringify(parsed, null, 2)}\n</script>`
        } catch(e) {
          html = "<!-- Invalid JSON -->"
        }
        break
      case "open_graph":
        try {
          const og = JSON.parse(value)
          html = Object.entries(og)
            .map(([key, val]) => `<meta property="${key}" content="${this.escapeHtml(val)}">`)
            .join("\n")
        } catch(e) {
          html = "<!-- Invalid JSON -->"
        }
        break
      default:
        html = this.escapeHtml(value)
    }
    
    this.previewTarget.querySelector("code").textContent = html
    
    // Update character count
    if (this.hasCharCountTarget) {
      const length = value.length
      let optimal = ""
      if (fixType === "title") {
        optimal = " (optimal: 50-60)"
      } else if (fixType === "meta_description") {
        optimal = " (optimal: 150-160)"
      }
      this.charCountTarget.textContent = `Characters: ${length}${optimal}`
    }
  }

  async save() {
    const newValue = this.inputTarget.value
    const fixId = this.element.dataset.fixId
    
    if (!newValue.trim()) {
      alert("Fix value cannot be empty")
      return
    }

    try {
      // Extract route params from URL
      const pathParts = window.location.pathname.split("/")
      const teamId = pathParts[1]
      const projectId = pathParts[3]
      const crawlId = pathParts[5]
      const auditId = pathParts[7]
      
      const url = `/${teamId}/projects/${projectId}/crawls/${crawlId}/site_audits/${auditId}/fixes/${fixId}`
      
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content
        },
        body: JSON.stringify({
          seo_fix: {
            user_value: newValue
          }
        })
      })

      if (response.ok) {
        // Update display
        this.displayValueTarget.textContent = newValue
        this.cancelEdit()
        
        // Show success feedback
        this.showFeedback("saved")
      } else {
        alert("Failed to save fix. Please try again.")
      }
    } catch (error) {
      console.error("Save error:", error)
      alert("An error occurred. Please try again.")
    }
  }

  showFeedback(type) {
    // Create temporary success indicator
    const feedback = document.createElement("span")
    feedback.className = "ml-2 text-xs text-green-400"
    feedback.textContent = "✓ Saved"
    this.editButtonTarget.parentElement.appendChild(feedback)
    
    setTimeout(() => feedback.remove(), 2000)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}

